import { EPartType, IOrder, IStoreItem } from "@drone-parts.garphild.pro/interfaces";
import { Select, Space, Typography, Button } from "antd";
import ordersStorage from "../storages/orders.storage";
import dayjs from "dayjs";

export enum ColumnTypes {
  delimiter = 'delimiter',
  text = 'text',
  number = 'number',
  select = 'select',
}

export interface ITemplateItem {
  title: string;
  dataIndex?: string;
  key: string;
  enabled?: boolean;
  realItem?: IStoreItem;
  type?: ColumnTypes;
  categories?: string[];
  parts?: string[];
  step?: number;
  defaultQuantity?: number;
  partGroup?: string;
  needQuantity?: boolean;
}

export interface IOrdersTableColumn {
  title: string;
  dataIndex: keyof IOrder;
  key: string;
  render?: (columnValue: any, record: IOrder, dataIndex: number) => JSX.Element | string | number | null;
}

export const batteryTableColumns: ITemplateItem[] = [
  {
    title: "Accumulators",
    dataIndex: "accumulators",
    key: "accumulators",
    enabled: true,
    type: ColumnTypes.select,
    categories: ['battery'],
    parts: [EPartType.accumulators],
  },
  {
    title: "Nickel strip",
    dataIndex: "nickelStrip",
    key: "nickelStrip",
    enabled: true,
    type: ColumnTypes.select,
    categories: ['battery'],
    parts: [EPartType.nickelStrip],
  },
  {
    title: "Balancing cable",
    dataIndex: "balancingCable",
    key: "balancingCable",
    enabled: true,
    type: ColumnTypes.select,
    categories: ['battery'],
    parts: [EPartType.balancingCable],
    defaultQuantity: 1,
  },
  {
    title: "Power cable",
    dataIndex: "powerCable",
    key: "powerCable",
    enabled: true,
    type: ColumnTypes.select,
    categories: ['battery'],
    parts: [EPartType.cable],
    defaultQuantity: 0.15,
  },
  {
    title: "Battery connector",
    dataIndex: "batteryConnector",
    key: "batteryConnector",
    enabled: true,
    type: ColumnTypes.select,
    categories: ['battery'],
    parts: [EPartType.xt60ConnectorF],
    defaultQuantity: 1,
  },
  {
    title: "Strong adhesive tape",
    dataIndex: "strongAdhesiveTape",
    key: "strongAdhesiveTape",
    enabled: true,
    type: ColumnTypes.select,
    categories: ['battery'],
    parts: [EPartType.strongAdhesiveTape],
  },
  {
    title: "Capton tape",
    dataIndex: "captonTape",
    key: "captonTape",
    enabled: true,
    type: ColumnTypes.select,
    categories: ['battery'],
    parts: [EPartType.adhesiveTape],
  },
  {
    title: "Carton",
    dataIndex: "carton",
    key: "carton",
    enabled: true,
    type: ColumnTypes.select,
    categories: ['battery'],
    parts: [EPartType.carton],
  },
  {
    title: "Hot glue",
    dataIndex: "hotGlue",
    key: "hotGlue",
    enabled: true,
    type: ColumnTypes.select,
    categories: ['battery'],
    parts: [EPartType.hotGlue],
  },
  {
    title: "Carton rings",
    dataIndex: "cartonRings",
    key: "cartonRings",
    enabled: true,
    type: ColumnTypes.select,
    categories: ['battery'],
    parts: [EPartType.cartonRings],
  },
  {
    title: "Inner heat shrink tube",
    dataIndex: "innerHeatShrinkTube",
    key: "innerHeatShrinkTube",
    enabled: true,
    type: ColumnTypes.select,
    categories: ['battery'],
    parts: [EPartType.heatShrink],
  },
  {
    title: "Outer heat shrink tube",
    dataIndex: "outerHeatShrinkTube",
    key: "outerHeatShrinkTube",
    enabled: true,
    type: ColumnTypes.select,
    categories: ['battery'],
    parts: [EPartType.heatShrink],
  },
  {
    title: "Battery strap",
    dataIndex: "batteryStrap",
    key: "batteryStrap",
    enabled: true,
    type: ColumnTypes.select,
    categories: ['battery'],
    parts: [EPartType.batteryStrap],
  },
];


export interface ITemplatePartItem {
  title: string;
  enabled: boolean;
  key: string;
  alternativeKeys?: string[];
  parts: string[];
  partGroup: string;
  type: ColumnTypes;
  needQuantity: boolean;
  defaultQuantity: number;
  step?: number;
}

export interface ITemplate {
  name: string;
  parts: ITemplatePartItem[];
}
export const defaultTemplate: ITemplate = {
  name: "default",
  parts: [
    {
      title: "Frame",
      enabled: true,
      key: 'frame',
      parts: [
        'frame:frame7',
        'frame:frame7rs',
        'frame:frame7small',
        'frame:frame7Big',
        'frame:frame8',
        'frame:frame8small',
        'frame:frame8Big',
        'frame:frame9',
        'frame:frame10',
        'frame:frame10small',
        'frame:frame10Big',
        'frame:frame10more',
      ],
      partGroup: 'frame',
      type: ColumnTypes.select,
      needQuantity: false,
      defaultQuantity: 1,
    },
    {
      title: "Stack",
      enabled: true,
      key: 'fcStack',
      parts: [
        'fcStack:fcStack45A',
        'fcStack:fcStack50A',
        'fcStack:fcStack55A',
        'fcStack:fcStack60A',
      ],
      partGroup: 'frame',
      type: ColumnTypes.select,
      needQuantity: false,
      defaultQuantity: 1,
    },
    {
      title: "Flight controller",
      enabled: true,
      key: 'fcSingle',
      alternativeKeys: ['fc', 'Flight controller'],
      parts: [
        'fcSingle:fc405',
        'fcSingle:fc422',
        'fcSingle:fcF7',
      ],
      partGroup: 'stack',
      type: ColumnTypes.select,
      needQuantity: false,
      defaultQuantity: 1,
    },
    {
      title: "ESC",
      enabled: true,
      key: 'esc',
      parts: [
        'esc:esc50A',
        'esc:esc55A',
        'esc:esc60A',
      ],
      type: ColumnTypes.select,
      partGroup: 'stack',
      needQuantity: false,
      defaultQuantity: 1,
    },
    {
      title: "Camera",
      enabled: true,
      key: 'camera',
      parts: [
        'camera:cameraSimple',
        'camera:cameraEvening',
        'camera:cameraNightIR',
        'camera:cameraThermal',
        'camera:cameraZoom',
        'camera:cameraSwitcher',
      ],
      type: ColumnTypes.select,
      partGroup: 'camera',
      needQuantity: false,
      defaultQuantity: 1,
    },
    {
      title: "Motors",
      enabled: true,
      key: 'motors',
      alternativeKeys: ['motor', 'Motor'],
      parts: [
        'motors:motors28',
        'motors:motors2810',
        'motors:motors2812',
        'motors:motors25',
        'motors:motors30',
        'motors:motors31',
      ],
      type: ColumnTypes.select,
      partGroup: 'camera',
      needQuantity: false,
      defaultQuantity: 1,
    },
    {
      title: "Receiver",
      enabled: true,
      key: 'receiver',
      // alternativeKeys: ['motor', 'Motor'],
      parts: [
        'receiver:receiverSimple',
        'receiver:receiverSimple868',
        'receiver:receiverSimple750',
        'receiver:receiverSimple433',
        'receiver:receiverDiversity750',
        'receiver:receiverDiversity868',
        'receiver:receiverDiversity',
        'receiver:receiverDiversity433',
        'receiver:receiverGeminy',
        'receiver:receiverCrossfire',
        'receiver:receiver24',
        'receiver:receiverOther',
      ],
      type: ColumnTypes.select,
      partGroup: 'receiver',
      needQuantity: false,
      defaultQuantity: 1,
    },
    {
      title: "Vtx",
      enabled: true,
      key: 'vtx',
      // alternativeKeys: ['motor', 'Motor'],
      parts: [
        'vtx:vtx49',
        'vtx:vtxSmall',
        'vtx:vtxBig',
        'vtx:vtxMiddle',
        'vtx:vtx33',
        'vtx:vtx33',
        'vtx:vtx12',
      ],
      type: ColumnTypes.select,
      partGroup: 'vtx',
      needQuantity: false,
      defaultQuantity: 1,
    },
    {
      title: "Pigtail",
      enabled: true,
      key: 'vtxAntennaConnector',
      // alternativeKeys: ['motor', 'Motor'],
      parts: [
        'vtx:antennaConnector',
        'mmcx-sma',
      ],
      type: ColumnTypes.select,
      partGroup: 'vtx',
      needQuantity: false,
      defaultQuantity: 1,
    },
    {
      title: "Condensator",
      enabled: true,
      key: 'condensator',
      alternativeKeys: ['Condensator'],
      parts: [
        'condensator:condensatorN',
        'condensator:condensatorB',
      ],
      type: ColumnTypes.select,
      partGroup: 'Other',
      needQuantity: false,
      defaultQuantity: 1,
    },
    {
      title: "Antenna for VTX",
      enabled: true,
      key: 'antennaVtx',
      alternativeKeys: ['vtxAntenna'],
      parts: [
        'antennaVtx:antennaVtxWide',
        'antennaVtx:antennaVtxRband',
        'antennaVtx:antennaVtxXLband',
        'antennaVtx:antennaVtxXband',
        'antennaVtx:antennaVtxLband',
        'antennaVtx:antennaVtxStandard',
        'antennaVtx:antennaVtx33',
        'antennaVtx:antennaVtx12',
      ],
      type: ColumnTypes.select,
      partGroup: 'Other',
      needQuantity: false,
      defaultQuantity: 1,
    },
    {
      title: "Propellers",
      enabled: true,
      key: 'propellers',
      // alternativeKeys: ['vtxAntenna'],
      parts: [
        'propellers:propellers7',
        'propellers:propellers8',
        'propellers:propellers9',
        'propellers:propellers10',
        'propellers:propellers10m',
      ],
      type: ColumnTypes.select,
      partGroup: 'Other',
      needQuantity: false,
      defaultQuantity: 1,
    },
    {
      title: "Battery RTU",
      enabled: true,
      key: 'battery',
      // alternativeKeys: ['vtxAntenna'],
      parts: [
        'battery:battery4s',
        'battery:batteryLiIon',
        'battery:battery6s',
      ],
      type: ColumnTypes.select,
      partGroup: 'Other',
      needQuantity: false,
      defaultQuantity: 1,
    },
  ]
};

export const droneTableColumns: ITemplateItem[] = [
  {
    title: "Frame",
    dataIndex: EPartType.frame,
    key: EPartType.frame,
    enabled: true,
    categories: ['drone'],
    parts: [
      EPartType.frame,
      EPartType.frame7,
      EPartType.frame7rs,
      EPartType.frame7Big,
      EPartType.frame7small,
      EPartType.frame8,
      EPartType.frame9,
      EPartType.frame10,
      EPartType.frame10more,
    ],
    type: ColumnTypes.select,
    partGroup: 'frame',
    needQuantity: false,
  },
  // {
  //   title: "Sponges",
  //   dataIndex: EPartType.sponges,
  //   key: EPartType.sponges,
  //   enabled: true,
  //   categories: ['drone'],
  //   parts: [EPartType.sponges],
  //   type: ColumnTypes.select,
  //   partGroup: 'frame',
  // },
  {
    title: "Cable sleeve",
    dataIndex: EPartType.cableSleeve,
    key: EPartType.cableSleeve,
    enabled: true,
    categories: ['drone'],
    parts: [EPartType.cableSleeve],
    type: ColumnTypes.select,
    step: 0.1,
    partGroup: 'frame',
    needQuantity: true,
  },
  {
    title: "FC/Stack",
    dataIndex: EPartType.fcStack,
    key: EPartType.fcStack,
    categories: ['drone'],
    parts: [
      'FC Stack',
      EPartType.fcStack,
      EPartType.fcStack45A,
      EPartType.fcStack50A,
      EPartType.fcStack55A,
      EPartType.fcStack60A,
    ],
    type: ColumnTypes.select,
    enabled: true,
    partGroup: 'stack',
  },
  {
    title: "FC",
    dataIndex: EPartType.fcSingle,
    key: EPartType.fcSingle,
    categories: ['drone'],
    parts: [EPartType.fcSingle,],
    type: ColumnTypes.select,
    enabled: true,
    partGroup: 'stack',
  },
  {
    title: "ESC",
    dataIndex: EPartType.esc,
    key: EPartType.esc,
    categories: ['drone'],
    parts: [
      EPartType.esc,
      EPartType.esc45A,
      EPartType.esc50A,
      EPartType.esc55A,
      EPartType.esc60A,
    ],
    type: ColumnTypes.select,
    enabled: true,
    partGroup: 'stack',
  },
  {
    title: "Condensator",
    dataIndex: EPartType.condensator,
    key: EPartType.condensator,
    enabled: true,
    categories: ['drone'],
    parts: [
      EPartType.condensator,
      EPartType.condensatorN,
      EPartType.condensatorB,
    ],
    type: ColumnTypes.select,
    partGroup: 'stack',
  },
  // {
  //   title: "FC dampers",
  //   dataIndex: "dampers",
  //   key: "dampers",
  //   enabled: true,
  //   type: ColumnTypes.select,
  //   categories: ['drone'],
  //   parts: [EPartType.dampers],
  //   partGroup: 'stack',
  // },
  // {
  //   title: "Self-locking nuts M3",
  //   dataIndex: "nuts",
  //   key: "nuts",
  //   enabled: true,
  //   type: ColumnTypes.select,
  //   categories: ['drone'],
  //   parts: [EPartType.nuts],
  //   partGroup: 'stack',
  // },
  {
    title: "Motor",
    dataIndex: EPartType.motors,
    key: EPartType.motors,
    enabled: true,
    type: ColumnTypes.select,
    categories: ['drone'],
    parts: [
      EPartType.motors,
      EPartType.motors25,
      EPartType.motors28,
      EPartType.motors2812,
      EPartType.motors31,
    ],
    partGroup: 'motorsKit',
  },
  {
    title: "Screws for motors",
    dataIndex: "screwsForMotors",
    key: "screwsForMotors",
    enabled: true,
    type: ColumnTypes.select,
    categories: ['drone'],
    parts: [EPartType.screws],
    partGroup: 'motorsKit',
  },
  // {
  //   title: "Nuts for motors",
  //   dataIndex: "nutsForMotors",
  //   key: "nutsForMotors",
  //   enabled: true,
  //   type: ColumnTypes.select,
  //   categories: ['drone'],
  //   parts: [EPartType.nuts],
  //   partGroup: 'motorsKit',
  // },
  {
    title: "Propellers",
    dataIndex: "propellers",
    key: "propellers",
    enabled: true,
    type: ColumnTypes.select,
    categories: ['drone'],
    parts: [
      EPartType.propellers,
      EPartType.propellers7,
      EPartType.propellers8,
      EPartType.propellers9,
      EPartType.propellers10,
    ],
    partGroup: 'other',
  },
  {
    title: "Camera",
    dataIndex: "camera",
    key: "camera",
    enabled: true,
    type: ColumnTypes.select,
    categories: ['drone'],
    parts: [
      EPartType.camera,
      EPartType.cameraSimple,
      EPartType.cameraEvening,
      EPartType.cameraNightIR,
      EPartType.cameraThermal,
      EPartType.cameraZoom,
    ],
    partGroup: 'cameraKit',
  },
  // {
  //   title: "Screws for Camera",
  //   dataIndex: "screwsForCamera",
  //   key: "screwsForCamera",
  //   enabled: true,
  //   type: ColumnTypes.select,
  //   categories: ['drone'],
  //   parts: [EPartType.screws],
  //   partGroup: 'cameraKit',
  // },
  {
    title: "Vtx",
    dataIndex: "vtx",
    key: "vtx",
    enabled: true,
    type: ColumnTypes.select,
    categories: ['drone'],
    parts: [
      EPartType.vtx,
      EPartType.vtxSmall,
      EPartType.vtxMiddle,
      EPartType.vtxBig,
    ],
    partGroup: 'vtxKit',
  },
  // {
  //   title: "Screws for Vtx",
  //   dataIndex: "screwsForVtx",
  //   key: "screwsForVtx",
  //   enabled: true,
  //   type: ColumnTypes.select,
  //   categories: ['drone'],
  //   parts: [EPartType.screws],
  //   partGroup: 'vtxKit',
  // },
  // {
  //   title: "Nuts for Vtx",
  //   dataIndex: "nutsForVtx",
  //   key: "nutsForVtx",
  //   enabled: true,
  //   type: ColumnTypes.select,
  //   categories: ['drone'],
  //   parts: [EPartType.nuts],
  //   partGroup: 'vtxKit',
  // },
  // {
  //   title: "Vtx mount",
  //   dataIndex: "vtxMount",
  //   key: "vtxMount",
  //   enabled: true,
  //   type: ColumnTypes.select,
  //   categories: ['drone'],
  //   parts: [EPartType.mounts],
  //   partGroup: 'vtxKit',
  // },
  {
    title: "VTX antenna",
    dataIndex: "vtxAntenna",
    key: "vtxAntenna",
    enabled: true,
    type: ColumnTypes.select,
    categories: ['drone'],
    parts: [EPartType.antenna],
    partGroup: 'vtxKit',
  },
  // {
  //   title: "VTX antenna mount",
  //   dataIndex: "vtxAntennaMount",
  //   key: "vtxAntennaMount",
  //   enabled: true,
  //   type: ColumnTypes.select,
  //   categories: ['drone'],
  //   parts: [EPartType.mounts],
  //   partGroup: 'vtxKit',
  // },
  {
    title: "VTX antenna connector",
    dataIndex: "vtxAntennaConnector",
    key: "vtxAntennaConnector",
    enabled: true,
    type: ColumnTypes.select,
    categories: ['drone'],
    parts: [EPartType.antennaConnector],
    partGroup: 'vtxKit',
  },
  {
    title: "Receiver",
    dataIndex: "receiver",
    key: "receiver",
    enabled: true,
    type: ColumnTypes.select,
    categories: ['drone'],
    parts: [
      EPartType.receiver,
      EPartType.receiverSimple,
      EPartType.receiverDiversity,
    ],
    partGroup: 'receiverKit',
  },
  // {
  //   title: "Receiver antena",
  //   dataIndex: "receiverAntena",
  //   key: "receiverAntena",
  //   enabled: true,
  //   type: ColumnTypes.select,
  //   categories: ['drone'],
  //   parts: [EPartType.antennaElrs],
  //   partGroup: 'receiverKit',
  // },
  // {
  //   title: "Antenna Mount",
  //   dataIndex: "recAntennaMount",
  //   key: "recAntennaMount",
  //   enabled: true,
  //   type: ColumnTypes.select,
  //   categories: ['drone'],
  //   parts: [EPartType.mounts],
  //   partGroup: 'receiverKit',
  // },
  {
    title: "Battery",
    dataIndex: EPartType.battery,
    key: EPartType.battery,
    enabled: true,
    type: ColumnTypes.select,
    categories: ['drone'],
    parts: [EPartType.battery],
    partGroup: 'batteryRtu',
  },
  // {
  //   title: "GPS",
  //   dataIndex: "gps",
  //   key: "gps",
  //   enabled: true,
  //   type: ColumnTypes.select,
  //   categories: ['battery'],
  //   parts: [EPartType.gps],
  //   partGroup: 'other',
  // },
  // {
  //   title: "GPS mount",
  //   dataIndex: "gpsMount",
  //   key: "gpsMount",
  //   enabled: true,
  //   type: ColumnTypes.select,
  //   categories: ['battery'],
  //   parts: [EPartType.mounts],
  //   partGroup: 'other',
  // },
];

export const defaultBatteryTemplate: ITemplate = {
  name: "defaultBattery",
  parts: [
    {
      title: "Accumulators",
      enabled: true,
      key: 'accumulators',
      parts: [
        'batteryComponents:accumulators',
        'batteryComponents:accumulators18650',
      ],
      partGroup: 'battery',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
    {
      title: "BMS",
      enabled: true,
      key: 'bms',
      parts: [
        'batteryComponents:BmsLifepo',
        'batteryComponents:BmsLi-ion',
      ],
      partGroup: 'battery',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
    {
      title: "Nickel strip",
      enabled: true,
      key: 'nickelStrip',
      parts: [
        'batteryComponents:nickelStrip',
        'batteryComponents:nickelStrip1510',
        'batteryComponents:NickelStrip0.2x30',
        'batteryComponents:nickelStrip333',
        'batteryComponents:nickelStrip28',
        'batteryComponents:Busbar',
      ],
      partGroup: 'battery',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
    {
      title: "Balancing cable",
      enabled: true,
      key: 'balancingCable',
      parts: [
        'batteryComponents:balancingCable',
        'batteryComponents:balancingCable4S',
        'batteryComponents:BalancerCable8s',
        'batteryComponents:AbClipForBalancingCable',
      ],
      partGroup: 'battery',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
    {
      title: "Power cable",
      enabled: true,
      key: 'powerCable',
      parts: [
        'cable:cablePower',
        'cable:other',
      ],
      partGroup: 'battery',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
      step: 0.05,
    },
    {
      title: "Battery connector",
      enabled: true,
      key: 'batteryConnector',
      parts: [
        'cable:xt60ConnectorF',
        'cable:xt60ConnectorM',
        'batteryComponents:AmassXt90',
      ],
      partGroup: 'battery',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
    {
      title: "Strong adhesive tape",
      enabled: true,
      key: 'strongAdhesiveTape',
      parts: [
        'batteryComponents:strongAdhesiveTape',
      ],
      partGroup: 'battery',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
    {
      title: "Capton tape",
      enabled: true,
      key: 'captonTape',
      parts: [
        'batteryComponents:captonTape',
        'batteryComponents:adhesiveTape',
      ],
      partGroup: 'battery',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
    {
      title: "Carton",
      enabled: true,
      key: 'carton',
      parts: [
        'batteryComponents:carton',
      ],
      partGroup: 'battery',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
    {
      title: "Carton rings",
      enabled: true,
      key: 'cartonRings',
      parts: [
        'batteryComponents:cartonRings',
        'batteryComponents:cartonRings18650',
      ],
      partGroup: 'battery',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
    {
      title: "Inner heat shrink tube",
      enabled: true,
      key: 'innerHeatShrinkTube',
      parts: [
        'batteryComponents:heatShrink',
        'batteryComponents:heatShrink120',
        'batteryComponents:HeatShrinkOther',
      ],
      partGroup: 'battery',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
    {
      title: "Outer heat shrink tube",
      enabled: true,
      key: 'outerHeatShrinkTube',
      parts: [
        'batteryComponents:heatShrink',
        'batteryComponents:heatShrink120',
        'batteryComponents:HeatShrinkOther',
      ],
      partGroup: 'battery',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
    {
      title: "Battery strap",
      enabled: true,
      key: 'batteryStrap',
      parts: [
        'batteryComponents:batteryStrap',
        'batteryComponents:batteryStrap250',
      ],
      partGroup: 'battery',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
    {
      title: "Electrical insulation tape",
      enabled: true,
      key: 'electricalInsulationTape',
      parts: [
        'equipment:ElectricalInsulationTape',
      ],
      partGroup: 'battery',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
  ]
};

export const defaultPowerbankTemplate: ITemplate = {
  name: "defaultPowerbank",
  parts: [
    {
      title: "Control board (BMS)",
      enabled: true,
      key: 'controlBoard',
      parts: [
        'batteryComponents:BmsLifepo',
        'batteryComponents:BmsLi-ion',
      ],
      partGroup: 'powerbank',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
    {
      title: "Case",
      enabled: true,
      key: 'case',
      parts: [
        'PowerbanksAndUps:PowerbankCase18650',
        'PowerbanksAndUps:PowerbankCase21700',
        'PowerbanksAndUps:12vCase',
      ],
      partGroup: 'powerbank',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
    {
      title: "Accumulators",
      enabled: true,
      key: 'accumulators',
      parts: [
        'batteryComponents:accumulators',
        'batteryComponents:accumulators18650',
      ],
      partGroup: 'powerbank',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
    {
      title: "Wire",
      enabled: true,
      key: 'wire',
      parts: [
        'cable:cablePower',
        'cable:other',
      ],
      partGroup: 'powerbank',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
      step: 0.05,
    },
    {
      title: "Nickel strip",
      enabled: true,
      key: 'nickelStrip',
      parts: [
        'batteryComponents:nickelStrip',
        'batteryComponents:nickelStrip1510',
        'batteryComponents:NickelStrip0.2x30',
        'batteryComponents:nickelStrip333',
        'batteryComponents:nickelStrip28',
        'batteryComponents:Busbar',
      ],
      partGroup: 'powerbank',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
    {
      title: "Springs",
      enabled: true,
      key: 'springs',
      parts: [],
      partGroup: 'powerbank',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
    {
      title: "PVC wrap",
      enabled: true,
      key: 'pvcWrap',
      parts: [
        'PowerbanksAndUps:21700PvcWrap',
      ],
      partGroup: 'powerbank',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
    {
      title: "Carton rings",
      enabled: true,
      key: 'cartonRings',
      parts: [
        'batteryComponents:cartonRings',
        'batteryComponents:cartonRings18650',
      ],
      partGroup: 'powerbank',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
    {
      title: "Insulation carton",
      enabled: true,
      key: 'insulationCarton',
      parts: [
        'batteryComponents:carton',
      ],
      partGroup: 'powerbank',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
    {
      title: "Tape",
      enabled: true,
      key: 'tape',
      parts: [
        'batteryComponents:captonTape',
        'batteryComponents:adhesiveTape',
        'batteryComponents:strongAdhesiveTape',
        'equipment:ElectricalInsulationTape',
      ],
      partGroup: 'powerbank',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
    {
      title: "Nylon tie",
      enabled: true,
      key: 'nylonTie',
      parts: [
        'equipment:nylonTie',
      ],
      partGroup: 'powerbank',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
  ]
};

export const defaultBatteryAssemblyTemplate: ITemplate = {
  name: "defaultBatteryAssembly",
  parts: [
    {
      title: "Accumulators",
      enabled: true,
      key: 'accumulators',
      parts: [
        'batteryComponents:accumulators',
        'batteryComponents:accumulators18650',
      ],
      partGroup: 'batteryAssembly',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
    {
      title: "BMS",
      enabled: true,
      key: 'bms',
      parts: [
        'batteryComponents:BmsLifepo',
        'batteryComponents:BmsLi-ion',
      ],
      partGroup: 'batteryAssembly',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
    {
      title: "Nickel strip",
      enabled: true,
      key: 'nickelStrip',
      parts: [
        'batteryComponents:nickelStrip',
        'batteryComponents:nickelStrip1510',
        'batteryComponents:NickelStrip0.2x30',
        'batteryComponents:nickelStrip333',
        'batteryComponents:nickelStrip28',
        'batteryComponents:Busbar',
      ],
      partGroup: 'batteryAssembly',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
    {
      title: "Balancing cable",
      enabled: true,
      key: 'balancingCable',
      parts: [
        'batteryComponents:balancingCable',
        'batteryComponents:balancingCable4S',
        'batteryComponents:BalancerCable8s',
        'batteryComponents:AbClipForBalancingCable',
      ],
      partGroup: 'batteryAssembly',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
    {
      title: "Power cable",
      enabled: true,
      key: 'powerCable',
      parts: [
        'cable:cablePower',
        'cable:other',
      ],
      partGroup: 'batteryAssembly',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
      step: 0.05,
    },
    {
      title: "Battery connector",
      enabled: true,
      key: 'batteryConnector',
      parts: [
        'cable:xt60ConnectorF',
        'cable:xt60ConnectorM',
        'batteryComponents:AmassXt90',
      ],
      partGroup: 'batteryAssembly',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
    {
      title: "Strong adhesive tape",
      enabled: true,
      key: 'strongAdhesiveTape',
      parts: [
        'batteryComponents:strongAdhesiveTape',
      ],
      partGroup: 'batteryAssembly',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
    {
      title: "Capton tape",
      enabled: true,
      key: 'captonTape',
      parts: [
        'batteryComponents:captonTape',
        'batteryComponents:adhesiveTape',
      ],
      partGroup: 'batteryAssembly',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
    {
      title: "Carton",
      enabled: true,
      key: 'carton',
      parts: [
        'batteryComponents:carton',
      ],
      partGroup: 'batteryAssembly',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
    {
      title: "Carton rings",
      enabled: true,
      key: 'cartonRings',
      parts: [
        'batteryComponents:cartonRings',
        'batteryComponents:cartonRings18650',
      ],
      partGroup: 'batteryAssembly',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
    {
      title: "Inner heat shrink tube",
      enabled: true,
      key: 'innerHeatShrinkTube',
      parts: [
        'batteryComponents:heatShrink',
        'batteryComponents:heatShrink120',
        'batteryComponents:HeatShrinkOther',
      ],
      partGroup: 'batteryAssembly',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
    {
      title: "Outer heat shrink tube",
      enabled: true,
      key: 'outerHeatShrinkTube',
      parts: [
        'batteryComponents:heatShrink',
        'batteryComponents:heatShrink120',
        'batteryComponents:HeatShrinkOther',
      ],
      partGroup: 'batteryAssembly',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
    {
      title: "Battery strap",
      enabled: true,
      key: 'batteryStrap',
      parts: [
        'batteryComponents:batteryStrap',
        'batteryComponents:batteryStrap250',
      ],
      partGroup: 'batteryAssembly',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
    {
      title: "Nylon tie",
      enabled: true,
      key: 'nylonTie',
      parts: [
        'equipment:nylonTie',
      ],
      partGroup: 'batteryAssembly',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
    {
      title: "Electrical insulation tape",
      enabled: true,
      key: 'electricalInsulationTape',
      parts: [
        'equipment:ElectricalInsulationTape',
      ],
      partGroup: 'batteryAssembly',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
  ]
};

export const defaultPowerAdapterTemplate: ITemplate = {
  name: "defaultPowerAdapter",
  parts: [
    {
      title: "Control board",
      enabled: true,
      key: 'controlBoard',
      parts: [
        'microcontrollers:Stm32',
        'microcontrollers:Esp32',
        'microcontrollers:Esp8266',
      ],
      partGroup: 'powerAdapter',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
    {
      title: "Voltage conversion board",
      enabled: true,
      key: 'voltageConversionBoard',
      parts: [
        'otherParts:PowerSupply',
      ],
      partGroup: 'powerAdapter',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
    {
      title: "Input connector",
      enabled: true,
      key: 'inputConnector',
      parts: [
        'cable:xt60ConnectorF',
        'cable:xt60ConnectorM',
        'batteryComponents:AmassXt90',
      ],
      partGroup: 'powerAdapter',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
    {
      title: "Input connector wires",
      enabled: true,
      key: 'inputConnectorWires',
      parts: [
        'cable:cablePower',
        'cable:other',
      ],
      partGroup: 'powerAdapter',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
      step: 0.05,
    },
    {
      title: "Output connector",
      enabled: true,
      key: 'outputConnector',
      parts: [
        'cable:xt60ConnectorF',
        'cable:xt60ConnectorM',
        'batteryComponents:AmassXt90',
      ],
      partGroup: 'powerAdapter',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
    {
      title: "Output connector wires",
      enabled: true,
      key: 'outputConnectorWires',
      parts: [
        'cable:cablePower',
        'cable:other',
      ],
      partGroup: 'powerAdapter',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
      step: 0.05,
    },
    {
      title: "Case",
      enabled: true,
      key: 'case',
      parts: [
        '3dPrinting:Parts',
        'PowerbanksAndUps:12vCase',
      ],
      partGroup: 'powerAdapter',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
    {
      title: "Insulation",
      enabled: true,
      key: 'insulation',
      parts: [
        'batteryComponents:captonTape',
        'batteryComponents:adhesiveTape',
        'equipment:ElectricalInsulationTape',
        'batteryComponents:heatShrink',
        'batteryComponents:heatShrink120',
        'batteryComponents:HeatShrinkOther',
      ],
      partGroup: 'powerAdapter',
      type: ColumnTypes.select,
      needQuantity: true,
      defaultQuantity: 1,
    },
  ]
};

export const DEFAULT_TEMPLATE_MAP: Record<string, ITemplate> = {
  drone: defaultTemplate,
  battery: defaultBatteryTemplate,
  powerbank: defaultPowerbankTemplate,
  'battery-assembly': defaultBatteryAssemblyTemplate,
  'power-adapter': defaultPowerAdapterTemplate,
};

export const ordersTableColumns: IOrdersTableColumn[] = [
    {
      title: "_id",
      dataIndex: "_id",
      key: "_id",
      render: (value: string, rowData: IOrder) => {
        return (
          <Space direction={"vertical"}>
            <Typography>{value}</Typography>
            <Space>
              <Button onClick={() => ordersStorage.hideRow(rowData._id)}>Hide</Button>
              <Button danger onClick={() => ordersStorage.deleteRow(rowData._id)}>Delete</Button>
            </Space>
          </Space>
        )
      }
    }, {
    title: "Marketplace",
    dataIndex: "marketplaceId",
    key: "marketplaceId",
    render: (value: string, row: IOrder) => {
      return (
        <Space direction={"vertical"}>
          <Typography>{value}</Typography>
          <Typography>{row.sellerId}</Typography>
        </Space>
      )
    }
  },
  {
    title: "Order Id",
    dataIndex: "orderId",
    key: "orderId",
    render: (orderId: string, row: IOrder) => {
      const trackingNumbers = row.trackingNumbers || [];
      return (
        <Space direction="vertical">
          {row.marketplaceId === 'aliexpress' ?
            <>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={`https://www.aliexpress.com/p/order/detail.html?orderId=${orderId}`
                }>
                {orderId}
              </a>
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={`https://www.aliexpress.com/p/tracking/index.html?tradeOrderId=${orderId}`
                }>
                tracking
              </a>
              <div>{trackingNumbers?.join(", ")}</div>
            </>
            : orderId
          }
        </Space>
      );
    }
  },
  {
    title: "Date",
    dataIndex: "orderDate",
    key: "orderDate",
  },
  {
    title: "Status",
    dataIndex: "orderStatus",
    key: "orderStatus",
    render: (value: string, row: IOrder) => {
      return (
        <Space direction="vertical">
          <Select
            value={value}
            onSelect={(value) => ordersStorage.setOrderStatus(row.orderId, row.marketplaceId, row._id.toString(), value)}
            options={ordersStorage.statuses.map((status) => ({ value: status, label: status }))}
          />
          <Typography>{row.trackingStatus?.split(" ").slice(0,7).join(" ")}</Typography>
        </Space>
      );
    }
  },
  {
    title: "Total",
    dataIndex: "orderTotal",
    key: "orderTotal",
    render: (value: number, row: IOrder) => {
      return (
        <Space direction="vertical" style={{whiteSpace: 'nowrap'}}>
          <Typography>Total: {row.orderCurrency}{value.toFixed(2)}</Typography>
          <Typography>Saved: {row.orderEconomy}</Typography>
        </Space>
      );
    }
  },
  {
    title: "Address",
    dataIndex: "orderShippingAddress",
    key: "orderShippingAddress",
    render: (value: string) => {
      return <Typography title={value}>{value ? `${value?.slice(0, 25)}...` : null}</Typography>;
    }
  },
  // {
  //   title: "trackingUrl",
  //   dataIndex: "trackingUrl",
  //   key: "trackingUrl",
  // },
  // {
  //   title: "trackingStatus",
  //   dataIndex: "trackingStatus",
  //   key: "trackingStatus",
  //   render: (value: string, rowData) => {
  //     switch (rowData.orderStatus) {
  //       case 'Completed':
  //         return 'Delivered';
  //       case 'To ship':
  //         return 'Not shipped';
  //       case 'Cancelled':
  //       case 'Expired':
  //         return 'Cancelled';
  //       default:
  //         return <Typography title={value}>{value && value?.split(' ').slice(0, 3).join(' ') + '...'}</Typography>;
  //     }
  //   }
  // },
  {
    title: "orderDate",
    dataIndex: "orderDate",
    key: "orderDate",
    render: (value: string, rowData) => (
      <Space direction="vertical">
        <Typography.Text>Ordered: {new Date(value).toISOString().slice(0, 10)}</Typography.Text>
        <Typography.Text>Estimated: {dayjs(rowData.orderDate).add(22, 'days').format("YYYY-MM-DD")}</Typography.Text>
      </Space>
    ),
  },
  {
    title: "category",
    dataIndex: "category",
    key: "category",
    render: (value: string, rowData) => {
      return (
        <>
          <Select
            showSearch
            value={value || null}
            onSelect={(value: string) => ordersStorage.setCategory(rowData.orderId, rowData.marketplaceId, value)}
            options={[
              { value: null, label: 'Select category' },
              { value: 'drones', label: 'drones' },
              { value: 'battery', label: 'battery' },
              { value: 'medicine', label: 'medicine' },
              { value: 'volunteer', label: 'volunteer' },
              { value: 'combined', label: 'combined' },
              { value: 'R&D', label: 'R&D' },
              { value: 'Production equipment', label: 'Production equipment' },
              { value: 'other', label: 'other' },
            ]}
          />
        </>
      )
    },
  },
];
